/**
 * Tenant AI usage / billing analytics (B0.4 / B3.1).
 */
import { get } from '@core/api/client';
import client from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type UsagePool = 'chat' | 'ingest';

export interface BillingUsageQuery {
  agentId?: string | null;
  from?: string | null;
  to?: string | null;
  pool?: UsagePool;
  page?: number;
  limit?: number;
}

export interface UsageTokenTotals {
  promptTokens: number;
  completionTokens: number;
  embeddingTokens: number;
  totalTokens: number;
  estimatedUsd: number;
  eventCount: number;
}

export interface UsagePoolBreakdown extends UsageTokenTotals {
  pool: string;
}

export interface UsageSummary {
  range: { from: string | null; to: string | null };
  tenantId: string | null;
  agentId: string | null;
  totals: UsageTokenTotals;
  byPool: UsagePoolBreakdown[];
}

export interface UsageByAgentRow extends UsageTokenTotals {
  agentId: string | null;
}

export interface UsageByOperationRow extends UsageTokenTotals {
  operation: string;
  pool: string;
}

export interface UsageEventRow {
  id: string;
  tenantId: string;
  agentId: string | null;
  sessionId: string | null;
  provider: string;
  operation: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  embeddingTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
  currency: string;
  billedTo: string;
  channel: string;
  pool: string;
  correlationId: string | null;
  sequence: number;
  createdAt: string;
}

export interface UsageEventsPage {
  items: UsageEventRow[];
  total: number;
  page: number;
  limit: number;
}

function toQuery(params: BillingUsageQuery): string {
  const q = new URLSearchParams();
  if (params.agentId) q.set('agentId', params.agentId);
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.pool) q.set('pool', params.pool);
  if (params.page != null) q.set('page', String(params.page));
  if (params.limit != null) q.set('limit', String(params.limit));
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const billingUsageService = {
  summary(params: BillingUsageQuery = {}): Promise<UsageSummary> {
    return get<UsageSummary>(
      `${endpoints.billingUsage.summary}${toQuery(params)}`,
    );
  },

  byAgent(params: BillingUsageQuery = {}): Promise<UsageByAgentRow[]> {
    return get<UsageByAgentRow[]>(
      `${endpoints.billingUsage.byAgent}${toQuery(params)}`,
    );
  },

  byOperation(params: BillingUsageQuery = {}): Promise<UsageByOperationRow[]> {
    return get<UsageByOperationRow[]>(
      `${endpoints.billingUsage.byOperation}${toQuery(params)}`,
    );
  },

  events(params: BillingUsageQuery = {}): Promise<UsageEventsPage> {
    return get<UsageEventsPage>(
      `${endpoints.billingUsage.events}${toQuery(params)}`,
    );
  },

  async exportCsv(params: BillingUsageQuery = {}): Promise<void> {
    const res = await client.get<Blob>(
      `${endpoints.billingUsage.exportCsv}${toQuery(params)}`,
      { responseType: 'blob' },
    );
    downloadBlob(res.data, 'ai-usage.csv');
  },
} as const;
